import React, { useEffect, useState, useCallback } from 'react';

/**
 * SuccessToast — animated success notification that auto-dismisses.
 * Props:
 *   visible    {boolean}  — show/hide
 *   message    {string}   — main message
 *   subMessage {string}   — optional subtitle
 *   onDismiss  {function} — called when toast hides
 *   duration   {number}   — ms before auto-dismiss (default 3500)
 *   position   {string}   — 'top' | 'bottom' (default 'top')
 *   variant    {string}   — 'success' | 'info' | 'warning' (default 'success')
 */
const SuccessToast = ({
  visible,
  message = 'Done!',
  subMessage,
  onDismiss,
  duration = 3500,
  position = 'top',
  variant = 'success',
}) => {
  const [show, setShow] = useState(false);
  const [exiting, setExiting] = useState(false);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setShow(false);
      setExiting(false);
      onDismiss?.();
    }, 300);
  }, [onDismiss]);

  useEffect(() => {
    if (visible) {
      setShow(true);
      setExiting(false);
      const t = setTimeout(dismiss, duration);
      return () => clearTimeout(t);
    } else {
      dismiss();
    }
  }, [visible]);

  if (!show) return null;

  const posClass = position === 'bottom' ?'bottom-6 left-1/2 -translate-x-1/2' :'top-6 left-1/2 -translate-x-1/2';

  const variantConfig = {
    success: {
      bg: 'bg-[#0a1628]',
      icon: (
        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" className="w-4 h-4">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      ),
      textColor: 'text-white',
      subColor: 'text-white/60',
    },
    info: {
      bg: 'bg-[#0a1628]',
      icon: (
        <div className="w-8 h-8 rounded-full bg-[#ffd700]/20 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="#ffd700" className="w-4 h-4">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
        </div>
      ),
      textColor: 'text-white',
      subColor: 'text-white/60',
    },
    warning: {
      bg: 'bg-[#0a1628]',
      icon: (
        <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="#f97316" className="w-4 h-4">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
        </div>
      ),
      textColor: 'text-white',
      subColor: 'text-white/60',
    },
  };

  const cfg = variantConfig?.[variant] || variantConfig?.success;

  return (
    <div
      className={`fixed ${posClass} z-[9999] transition-all duration-300 ease-out ${
        exiting ? 'opacity-0 -translate-y-3 scale-95' : 'opacity-100 translate-y-0 scale-100'
      }`}
      style={{ transform: exiting ? 'translateX(-50%) translateY(-12px) scale(0.95)' : 'translateX(-50%) translateY(0) scale(1)' }}
    >
      <div className={`${cfg?.bg} rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 min-w-[260px] max-w-sm border border-white/10`}>
        {cfg?.icon}
        <div className="flex-1 min-w-0">
          <p className={`${cfg?.textColor} font-semibold text-sm leading-tight`}>{message}</p>
          {subMessage && <p className={`${cfg?.subColor} text-xs mt-0.5 leading-tight`}>{subMessage}</p>}
        </div>
        <button
          onClick={dismiss}
          className="text-white/30 hover:text-white/70 transition-colors flex-shrink-0 ml-1"
          aria-label="Dismiss"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SuccessToast;
